import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Divider, Typography, Chip, Button } from "@mui/material";
import { IoSettingsOutline } from "react-icons/io5";
import PersonalDetailsForm from "../../components/FormComponents/PersonalDetailsForm";
import FamilyDetailsForm from "../../components/FormComponents/FamilyDetailsForm";
import MaternalDetailsForm from "../../components/FormComponents/MaternalDetailsForm";
import OtherDetailsForm from "../../components/FormComponents/OtherDetailsForm";
import { NavLink, useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { userImagesUpload } from "../../Redux/Actions/AuthAction";
import { useDispatch, useSelector } from "react-redux";
import { getDropdown, getUserProfileById, updateUserProfile } from "../../Redux/Actions/UsersProfileAction";
import useTitle from "../../Hooks/useTitle";
import { FaArrowLeft, FaEye } from "react-icons/fa";

const AdminEditProfile = () => {
  useTitle('Admin - Edit User');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: userId } = useParams();

  const Auth = useSelector((state) => state.Auth);
  const UsersProfile = useSelector((state) => state.UsersProfile);

  const [castData, setCastData] = useState([]);
  const [religionData, setReligionData] = useState([]);
  const [dietData, setDietData] = useState([]);
  const [maritalData, setMaritalData] = useState([]);
  const [formData, setFormData] = useState({});
  const [userStatus, setUserStatus] = useState('pending');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sender Image States (optional)
  const [senderPreview, setSenderPreview] = useState(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [facingMode, setFacingMode] = useState("user");

  // Decode user ID from base64
  const decodeUserId = (encodedId) => {
    try {
      if (!encodedId) return null;
      const standardBase64 = encodedId.replace(/-/g, '+').replace(/_/g, '/');
      const padded = standardBase64.padEnd(standardBase64.length + (4 - standardBase64.length % 4) % 4, '=');
      return parseInt(atob(padded));
    } catch (error) {
      console.error('Error decoding user ID:', error);
      return null;
    }
  };

  const transformApiData = (apiData) => ({
    firstname: apiData.firstname || '',
    lastname: apiData.lastname || '',
    email: apiData.email || '',
    gender: apiData.profile?.gender || "Male",
    date_of_birth: apiData.profile?.date_of_birth || '',
    religion: apiData.profile?.religion || '',
    cast: apiData.profile?.cast || '',
    height: parseFloat(apiData.profile?.height) || '',
    weight: parseFloat(apiData.profile?.weight) || '',
    country_code: apiData.profile?.country_code || '',
    phone_number: apiData.profile?.phone_number || '',
    city: apiData.profile?.city || '',
    country: apiData.profile?.country || '',
    years_in_country: apiData.profile?.years_in_country || '',
    visa_status: apiData.profile?.visa_status || '',
    nationality: apiData.profile?.nationality || '',
    diet: apiData.profile?.diet || '',
    marital_status: apiData.profile?.marital_status || '',
    live_with_family: apiData.profile?.live_with_family === 1 || apiData.profile?.live_with_family === "1",
    hobbies: apiData.profile?.hobbies || '',
    extra_skills: apiData.profile?.extra_skills || '',
    status: apiData.status || 'pending',

    // Arrays
    educations: Array.isArray(apiData.educations) ? apiData.educations : [],
    highschools: Array.isArray(apiData.highschools) ? apiData.highschools : [],
    occupations: Array.isArray(apiData.occupations) ? apiData.occupations : [],
    rewards: Array.isArray(apiData.rewards) ? apiData.rewards : [],
    awards: Array.isArray(apiData.awards) ? apiData.awards : [],
    medals: Array.isArray(apiData.medals) ? apiData.medals : [],
    certifications: Array.isArray(apiData.certifications) ? apiData.certifications : [],

    // Family fields
    father_name: apiData.family?.father_name || '',
    father_occupation: apiData.family?.father_occupation || '',
    father_phone_number: apiData.family?.father_phone_number || '',
    father_country_code: apiData.family?.father_country_code || '',
    mother_name: apiData.family?.mother_name || '',
    mother_occupation: apiData.family?.mother_occupation || '',
    mother_phone_number: apiData.family?.mother_phone_number || '',
    mother_country_code: apiData.family?.mother_country_code || '',
    parents_native: apiData.family?.parents_native || '',
    parents_cast: apiData.family?.parents_cast || '',
    maternal_uncle_name: apiData.family?.maternal_uncle_name || '',
    maternal_uncle_occupation: apiData.family?.maternal_uncle_occupation || '',
    maternal_uncle_native_place: apiData.family?.maternal_uncle_native_place || '',
    maternal_phone_number: apiData.family?.maternal_phone_number || '',
    maternal_country_code: apiData.family?.maternal_country_code || '',

    // Other details
    partners_parents_origin_choice: apiData.other_details?.partners_parents_origin_choice || '',
    expected_education: apiData.other_details?.expected_education || '',
    senders_name: apiData.other_details?.senders_name || '',
    senders_country_code: apiData.other_details?.senders_country_code || '',
    senders_phone_number: apiData.other_details?.senders_phone_number || '',
    person_to_contact_name: apiData.other_details?.person_to_contact_name || '',
    person_to_contact_country_code: apiData.other_details?.person_to_contact_country_code || '',
    person_to_contact_phone: apiData.other_details?.person_to_contact_phone || '',

    // Social media
    instagram: apiData.other_details?.instagram || '',
    linkedin: apiData.other_details?.linkedin || '',
    snapchat: apiData.other_details?.snapchat || '',
    twitter: apiData.other_details?.twitter || '',
    facebook: apiData.other_details?.facebook || '',

    // Sender image - check both locations (optional)
    sender_image: apiData.other_details?.sender_image || apiData.sender_image || '',
  });

  // Transform form data to API payload
  const transformFormDataToApiPayload = (formData, dateOfBirth, height, images) => {
    const formattedDateOfBirth = `${dateOfBirth.year}-${dateOfBirth.month.padStart(2, '0')}-${dateOfBirth.day.padStart(2, '0')}`;
    const formattedHeight = parseFloat(`${height.ft}.${height.in}`);
    
    const payload = {
      // Personal details
      firstname: formData.firstname || '',
      lastname: formData.lastname || '',
      
      // Profile details
      gender: formData.gender || "Male",
      date_of_birth: formattedDateOfBirth,
      religion: formData.religion || '',
      cast: formData.cast || '',
      height: formattedHeight,
      weight: parseFloat(formData.weight) || 0,
      country_code: formData.country_code || '',
      phone_number: formData.phone_number || '',
      city: formData.city || '',
      native_place: formData.native_place || formData.city || '',
      country: formData.country || '',
      years_in_country: formData.years_in_country || '',
      visa_status: formData.visa_status || '',
      nationality: formData.nationality || '',
      diet: formData.diet || '',
      marital_status: formData.marital_status || '',
      live_with_family: Boolean(formData.live_with_family),
      hobbies: formData.hobbies || '',
      extra_skills: formData.extra_skills || '',

      // Education
      educations: educationList
        .filter(edu => edu.degree && edu.university_name)
        .map(edu => ({
          degree: edu.degree || '',
          from_year: parseInt(edu.from_year) || 0,
          to_year: parseInt(edu.to_year) || 0,
          university_name: edu.university_name || '',
          city: edu.city || '',
          college: edu.college || ''
        })),

      // Occupation
      occupations: occupationList
        .filter(occ => occ.role && occ.company_name)
        .map(occ => ({
          role: occ.role || '',
          from_year: parseInt(occ.from_year) || 0,
          to_year: parseInt(occ.to_year) || 0,
          company_name: occ.company_name || ''
        })),

      // Family details
      father_name: formData.father_name || '',
      father_occupation: formData.father_occupation || '',
      father_phone_number: formData.father_phone_number || '',
      father_country_code: formData.father_country_code || '',
      mother_name: formData.mother_name || '',
      mother_occupation: formData.mother_occupation || '',
      mother_phone_number: formData.mother_phone_number || '',
      mother_country_code: formData.mother_country_code || '',
      parents_native: formData.parents_native || '',
      parents_cast: formData.parents_cast || '',
      maternal_uncle_name: formData.maternal_uncle_name || '',
      maternal_uncle_occupation: formData.maternal_uncle_occupation || '',
      maternal_uncle_native_place: formData.maternal_uncle_native_place || '',
      maternal_phone_number: formData.maternal_phone_number || '',
      maternal_country_code: formData.maternal_country_code || '',

      // Other details
      partners_parents_origin_choice: formData.partners_parents_origin_choice || '',
      expected_education: formData.expected_education || '',
      senders_name: formData.senders_name || '',
      senders_country_code: formData.senders_country_code || '',
      senders_phone_number: formData.senders_phone_number || '',
      person_to_contact_name: formData.person_to_contact_name || '',
      person_to_contact_country_code: formData.person_to_contact_country_code || '',
      person_to_contact_phone: formData.person_to_contact_phone || '',

      // Social media
      instagram: formData.instagram || '',
      linkedin: formData.linkedin || '',
      snapchat: formData.snapchat || '',
      twitter: formData.twitter || '',
      facebook: formData.facebook || '',

      // Sender image (optional)
      sender_image: formData.sender_image || '',

      // Awards and achievements
      rewards: rewardsList
        .filter(reward => reward.title)
        .map(reward => ({
          title: reward.title || '',
          year: parseInt(reward.year) || 0
        })),

      awards: awardsList
        .filter(award => award.title)
        .map(award => ({
          title: award.title || '',
          year: parseInt(award.year) || 0
        })),

      medals: medalsList
        .filter(medal => medal.title)
        .map(medal => ({
          title: medal.title || '',
          year: parseInt(medal.year) || 0
        })),

      certifications: certificationsList
        .filter(cert => cert.title)
        .map(cert => ({
          title: cert.title || '',
          year: parseInt(cert.year) || 0
        })),

      highschools: highschoolList
        .filter(school => school.school_name)
        .map(school => ({
          school_name: school.school_name || '',
          from_year: parseInt(school.from_year) || 0,
          to_year: parseInt(school.to_year) || 0,
          city: school.city || ''
        })),

      // Images - Use the correct key "images" instead of "image_urls"
      // images_urls: images
      //   .filter(img => img !== null && typeof img === 'string')
      //   .map((img, index) => ({
      //     url: img,
      //     index: index
      //   }))
    };

    // Remove empty arrays and empty strings
    Object.keys(payload).forEach(key => {
      if (Array.isArray(payload[key]) && payload[key].length === 0) {
        delete payload[key];
      }
      if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
        delete payload[key];
      }
    });

    return payload;
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        const decodedUserId = decodeUserId(userId);
        if (!decodedUserId) {
          toast.error("Invalid user ID");
          navigate('/admin');
          return;
        }

        await dispatch(getUserProfileById(decodedUserId));

      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to fetch user data");
      } finally {
        setIsLoading(false);
      }
    };

    const fetchDropDown = async () => {
      try {
        const res = await dispatch(getDropdown());
        if (res?.data?.data?.cast) {
          setCastData(res.data.data.cast);
          setReligionData(res.data.data.religion);
          setDietData(res.data.data.diet);
          setMaritalData(res.data.data.marital);
        }
      } catch (error) {
        console.error("Failed to fetch dropdown data:", error);
      }
    };

    fetchUserData();
    fetchDropDown();
  }, [dispatch, userId, navigate]);

  useEffect(() => {
    if (UsersProfile?.userProfileById?.data) {
      const transformedData = transformApiData(UsersProfile.userProfileById.data);
      setFormData(transformedData);
      setUserStatus(transformedData.status || 'pending');

      // Initialize all lists
      setEducationList(transformedData.educations || []);
      setOccupationList(transformedData.occupations || []);
      setAwardsList(transformedData.awards || []);
      setRewardsList(transformedData.rewards || []);
      setMedalsList(transformedData.medals || []);
      setCertificationsList(transformedData.certifications || []);
      setHighschoolList(transformedData.highschools || []);

      // Set images - handle both array format and object format
      const userImages = UsersProfile.userProfileById.data.images;
      if (userImages && Array.isArray(userImages)) {
        if (userImages.length === 0) {
          setImages([null, null, null, null]);
        } else {
          // If images are objects with url property, extract URLs
          const imageUrls = userImages.map(img => 
            typeof img === 'object' ? img.url : img
          );
          // Fill remaining slots with null
          const filledImages = [...imageUrls];
          while (filledImages.length < 4) {
            filledImages.push(null);
          }
          setImages(filledImages.slice(0, 4));
        }
      } else {
        setImages([null, null, null, null]);
      }

      // Set sender preview - check if image exists (optional)
      if (transformedData.sender_image) {
        setSenderPreview(transformedData.sender_image);
        console.log('Sender image found:', transformedData.sender_image);
      } else {
        setSenderPreview(null);
        console.log('No sender image found');
      }

      // Set date of birth
      if (transformedData.date_of_birth) {
        const date = transformedData.date_of_birth.split("-");
        setDateOfBirth({
          day: date[2] || "01",
          month: date[1] || "01",
          year: date[0] || "1990",
        });
      }

      // Set height
      if (transformedData.height) {
        const height = transformedData.height.toString().split(".");
        setHeight({
          ft: height[0] || "5",
          in: height[1] || "8",
        });
      }
    }
  }, [UsersProfile.userProfileById]);

  const [images, setImages] = useState([null, null, null, null]);
  const [dateOfBirth, setDateOfBirth] = useState({
    day: "01",
    month: "01",
    year: "1990",
  });
  const [height, setHeight] = useState({
    ft: "5",
    in: "8",
  });

  // Menu items for admin - Only Edit Profile
  const menuItems = [
    { label: "Edit Profile", path: "Edit-Profile", icon: <IoSettingsOutline size={20} /> },
  ];

  const buttonRefs = useRef([]);

  const handleClick = (tabName, index) => {
    if (buttonRefs.current[index]) {
      buttonRefs.current[index].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  };

  // View user profile
  const handleViewProfile = () => {
    navigate(`/user/${userId}`);
  };

  // Phone number handlers and other form handlers
  const handlePhoneNumberChange = (field, newPhone) => {
    setFormData((prev) => ({ ...prev, [field]: newPhone }));
  };

  const handleCodeChange = (field, newCode) => {
    setFormData((prev) => ({ ...prev, [field]: newCode }));
  };

  const handleCountryChange = (field, newCountry) => {
    setFormData((prev) => ({ ...prev, [field]: newCountry }));
  };

  // Dynamic Education Fields
  const [educationList, setEducationList] = useState([]);
  const handleAddEducation = () => {
    setEducationList((prev) => [
      ...prev,
      { degree: "", from_year: "", to_year: "", university_name: "", city: "", college: "" },
    ]);
  };
  const handleRemoveEducation = (index) => {
    setEducationList((prev) => prev.filter((_, i) => i !== index));
  };
  const handleEducationChange = (index, event) => {
    const { name, value } = event.target;
    setEducationList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  // Dynamic Awards Fields
  const [awardsList, setAwardsList] = useState([]);
  const handleAddAwards = () => {
    setAwardsList((prev) => [
      ...prev,
      { title: "", year: "" },
    ]);
  };
  const handleRemoveAwards = (index) => {
    setAwardsList((prev) => prev.filter((_, i) => i !== index));
  };
  const handleAwardsChange = (index, event) => {
    const { name, value } = event.target;
    setAwardsList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  // Dynamic Rewards Fields
  const [rewardsList, setRewardsList] = useState([]);
  const handleAddRewards = () => {
    setRewardsList((prev) => [
      ...prev,
      { title: "", year: "" },
    ]);
  };
  const handleRemoveRewards = (index) => {
    setRewardsList((prev) => prev.filter((_, i) => i !== index));
  };
  const handleRewardsChange = (index, event) => {
    const { name, value } = event.target;
    setRewardsList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  // Dynamic Medals Fields
  const [medalsList, setMedalsList] = useState([]);
  const handleAddMedals = () => {
    setMedalsList((prev) => [
      ...prev,
      { title: "", year: "" },
    ]);
  };
  const handleRemoveMedals = (index) => {
    setMedalsList((prev) => prev.filter((_, i) => i !== index));
  };
  const handleMedalsChange = (index, event) => {
    const { name, value } = event.target;
    setMedalsList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  // Dynamic Certifications Fields
  const [certificationsList, setCertificationsList] = useState([]);
  const handleAddCertifications = () => {
    setCertificationsList((prev) => [
      ...prev,
      { title: "", year: "" },
    ]);
  };
  const handleRemoveCertifications = (index) => {
    setCertificationsList((prev) => prev.filter((_, i) => i !== index));
  };
  const handleCertificationsChange = (index, event) => {
    const { name, value } = event.target;
    setCertificationsList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  // Dynamic Occupation Fields
  const [occupationList, setOccupationList] = useState([]);
  const handleAddOccupation = () => {
    setOccupationList((prev) => [
      ...prev,
      { role: "", from_year: "", to_year: "", company_name: "" },
    ]);
  };
  const handleRemoveOccupation = (index) => {
    setOccupationList((prev) => prev.filter((_, i) => i !== index));
  };
  const handleOccupationChange = (index, event) => {
    const { name, value } = event.target;
    setOccupationList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  const [highschoolList, setHighschoolList] = useState([]);
  const handleHighschoolChange = (index, event) => {
    const { name, value } = event.target;
    setHighschoolList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [name]: value };
      return updated;
    });
  };

  // Form handlers
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === "live_with_family") {
      setFormData((prev) => ({ ...prev, [name]: value === "true" }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value || '' }));
    }
  };

  const handleDateChange = (event) => {
    const { name, value } = event.target;
    setDateOfBirth((prev) => ({ ...prev, [name]: value }));
  };

  const handleHeightChange = (event) => {
    const { name, value } = event.target;
    setHeight((prev) => ({ ...prev, [name]: value }));
  };

  // Sender Image Handlers (optional)
  const handleTakePhoto = async () => {
    setIsCameraOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          facingMode: facingMode
        } 
      });
      videoRef.current.srcObject = stream;
      videoRef.current.play();
    } catch (error) {
      console.error("Camera access error:", error);
      toast.error("Unable to access camera. Please allow camera permissions.");
    }
  };

  const switchCamera = async () => {
    const newFacingMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(newFacingMode);
    
    const currentStream = videoRef.current.srcObject;
    if (currentStream) {
      currentStream.getTracks().forEach(track => track.stop());
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          facingMode: newFacingMode
        } 
      });
      videoRef.current.srcObject = stream;
      videoRef.current.play();
    } catch (error) {
      console.error("Error switching camera:", error);
      toast.error("Unable to switch camera. Your device might not support multiple cameras.");
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const context = canvas.getContext("2d");
    
    if (facingMode === "user") {
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
    }
    
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    if (facingMode === "user") {
      context.setTransform(1, 0, 0, 1, 0, 0);
    }
    
    const imageData = canvas.toDataURL("image/jpeg", 0.92);

    const stream = video.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }

    setIsCameraOpen(false);
    setSenderPreview(imageData);
    setFormData(prev => ({ ...prev, sender_image: imageData }));
  };

  const cancelCamera = () => {
    const stream = videoRef.current.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    setIsCameraOpen(false);
  };

  // Handle sender image removal (optional)
  const handleRemoveSenderImage = () => {
    setSenderPreview(null);
    setFormData(prev => ({ ...prev, sender_image: '' }));
  };

  // Submit handler with API call
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      const decodedUserId = decodeUserId(userId);
      
      if (!decodedUserId) {
        toast.error("Invalid user ID");
        return;
      }

      // Prepare image FormData
      const imageData = new FormData();
      let hasNewImages = false;
      images.forEach((file, index) => {
        if (typeof file === "string") {
          imageData.append(`image_urls[]`, file);
        } else if (file instanceof File) {
          imageData.append(`images[]`, file);
          imageData.append(`image_indexes[]`, index);
          hasNewImages = true;
        }
      });

      // 1. First: Upload images if any new files
      let uploadedImageResult = null;
      if (hasNewImages) {
        const res1 = await dispatch(userImagesUpload(imageData));
        if (res1?.data?.success) {
          toast.success("Images uploaded successfully!");
          uploadedImageResult = res1.data.data;
        } else {
          toast.error("Failed to upload images");
          return;
        }
      }

      // 2. Second: Update profile with normal payload
      const apiPayload = transformFormDataToApiPayload(formData, dateOfBirth, height, images);
      const result = await dispatch(updateUserProfile(decodedUserId, apiPayload));

      if (!result?.data?.success) {
        toast.error(result?.data?.message || "Failed to update profile");
        return;
      }

      // 3. Third: If images were involved, call updateUserProfile again with image FormData
      if (hasNewImages || images.some(img => typeof img === 'string')) {
        const finalImageData = new FormData();
        images.forEach((file, index) => {
          if (typeof file === "string") {
            finalImageData.append(`image_urls[]`, file);
          } else if (file instanceof File) {
            finalImageData.append(`images[]`, file);
            finalImageData.append(`image_indexes[]`, index);
          }
        });

        console.log("finalImageData :", finalImageData);
        

        const imageUpdateResult = await dispatch(updateUserProfile(decodedUserId, finalImageData, true));
        
        if (imageUpdateResult?.data?.success) {
          toast.success("Profile and images updated successfully!");
        } else {
          toast.warn("Profile updated, but image sync failed.");
        }
      } else {
        toast.success("Profile updated successfully!");
      }

      // Navigate after success
      setTimeout(() => {
        navigate('/admin');
      }, 1500);
      
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error?.response?.data?.message || "Failed to update profile");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Status chip component
  const StatusChip = ({ status }) => {
    const statusConfig = {
      approved: { color: '#10b981', bgColor: '#ecfdf5', label: 'Approved' },
      rejected: { color: '#ef4444', bgColor: '#fef2f2', label: 'Rejected' },
      pending: { color: '#f59e0b', bgColor: '#fffbeb', label: 'Pending Review' }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Chip
        label={config.label}
        sx={{
          backgroundColor: config.bgColor,
          color: config.color,
          fontWeight: '600',
          border: `1px solid ${config.color}20`
        }}
      />
    );
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="h-[60vh] w-full flex items-center justify-center">
          <CircularProgress size={40} />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header with Back Button and User Info */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin')}
              className="hover:text-[#E72848] transition-colors flex items-center group"
            >
              <FaArrowLeft
                size={20}
                className="group-hover:-translate-x-0.5 transition-transform"
              />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Edit User Profile
              </h1>
              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mt-2">
                <Typography variant="body1" className="text-gray-600 font-medium">
                  {formData.firstname} {formData.lastname}
                </Typography>
                <StatusChip status={userStatus} />
                <Typography variant="body2" className="text-gray-500">
                  {formData.email}
                </Typography>
              </div>
            </div>
          </div>
          <Button
            variant="outlined"
            startIcon={<FaEye />}
            onClick={handleViewProfile}
            className="border-[#E72848] text-[#E72848] hover:bg-[#E72848] hover:text-white"
          >
            View Public Profile
          </Button>
        </div>
      </div>

      {/* Mobile Settings Menu - Removed since only one option */}
      <div className="block lg:hidden mb-6">
        <div className="flex items-center p-1 rounded-lg max-w-max justify-start overflow-auto gap-4 border bg-gray-50">
          {menuItems.map((item, index) => (
            <NavLink
              key={item.label}
              to={`/admin/edit-user/${userId}/${item.path}`}
              ref={(el) => (buttonRefs.current[index] = el)}
              onClick={() => handleClick(item.label, index)}
              className="flex flex-col items-center text-sm font-medium whitespace-nowrap p-3 rounded-md transition-colors text-[#E72848] bg-white border border-[#E72848] shadow-sm"
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* LEFT SECTION - Sidebar */}
        <div className="w-full lg:w-64 hidden lg:block">
          <div className="sticky top-4">
            <Box className="p-4 bg-white rounded-lg shadow-sm border">
              <div className="mb-4">
                <Typography variant="h6" className="font-bold text-gray-900">
                  User Management
                </Typography>
              </div>
              <div className="w-full">
                {menuItems.map((item) => (
                  <NavLink
                    to={`/admin/edit-user/${userId}/${item.path}`}
                    key={item.label}
                    className="flex items-center gap-2 cursor-pointer p-3 rounded-xl font-bold my-2 w-full whitespace-nowrap transition-colors text-white bg-[#E72848]"
                  >
                    {item.icon}
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </Box>
          </div>
        </div>

        <Divider orientation="vertical" flexItem className="hidden lg:block" />

        {/* RIGHT SECTION: Content - Only Edit Profile */}
        <div className="w-full flex-1">
          <div className="w-full grid grid-cols-1 gap-5">
            <form className="w-full" onSubmit={handleSubmit}>
              <PersonalDetailsForm
                formData={formData}
                handleFormChange={handleFormChange}
                images={images}
                setImages={setImages}
                dateOfBirth={dateOfBirth}
                handleDateChange={handleDateChange}
                height={height}
                handleHeightChange={handleHeightChange}
                educationList={educationList}
                handleEducationChange={handleEducationChange}
                handleAddEducation={handleAddEducation}
                handleRemoveEducation={handleRemoveEducation}
                occupationList={occupationList}
                handleOccupationChange={handleOccupationChange}
                handleAddOccupation={handleAddOccupation}
                handleRemoveOccupation={handleRemoveOccupation}
                highschoolList={highschoolList}
                handleHighschoolChange={handleHighschoolChange}
                awardsList={awardsList}
                handleAwardsChange={handleAwardsChange}
                handleAddAwards={handleAddAwards}
                handleRemoveAwards={handleRemoveAwards}
                rewardsList={rewardsList}
                handleRewardsChange={handleRewardsChange}
                handleAddRewards={handleAddRewards}
                handleRemoveRewards={handleRemoveRewards}
                medalsList={medalsList}
                handleMedalsChange={handleMedalsChange}
                handleAddMedals={handleAddMedals}
                handleRemoveMedals={handleRemoveMedals}
                certificationsList={certificationsList}
                handleCertificationsChange={handleCertificationsChange}
                handleAddCertifications={handleAddCertifications}
                handleRemoveCertifications={handleRemoveCertifications}
                handlePhoneNumberChange={handlePhoneNumberChange}
                handleCodeChange={handleCodeChange}
                handleCountryChange={handleCountryChange}
                castData={castData}
                religionData={religionData}
                dietData={dietData}
                maritalData={maritalData}
              />
              <FamilyDetailsForm
                formData={formData}
                handleFormChange={handleFormChange}
                handlePhoneNumberChange={handlePhoneNumberChange}
                handleCodeChange={handleCodeChange}
                handleCountryChange={handleCountryChange}
                castData={castData}
              />
              <MaternalDetailsForm
                formData={formData}
                handleFormChange={handleFormChange}
                handleCodeChange={handleCodeChange}
                handleCountryChange={handleCountryChange}
                handlePhoneNumberChange={handlePhoneNumberChange}
              />
              <OtherDetailsForm
                formData={formData}
                handleFormChange={handleFormChange}
                handlePhoneNumberChange={handlePhoneNumberChange}
                handleCodeChange={handleCodeChange}
                handleCountryChange={handleCountryChange}

                // Optional sender image props
                senderPreview={senderPreview}
                handleTakePhoto={handleTakePhoto}
                isCameraOpen={isCameraOpen}
                cancelCamera={cancelCamera}
                capturePhoto={capturePhoto}
                switchCamera={switchCamera}
                facingMode={facingMode}
                videoRef={videoRef}
                canvasRef={canvasRef}
                onRemoveImage={handleRemoveSenderImage}
              />

              <div className="max-w-max m-auto">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#E72848] text-white py-3 px-12 rounded-lg font-bold flex justify-center transition-all duration-200 transform hover:scale-[1.02] active:scale-95 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <CircularProgress color="inherit" size={24} />
                  ) : (
                    "Update Profile"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEditProfile;